import React, { useMemo, useRef } from "react";
import { BezierCurve } from "../../bezier/bezier";
import { useBezierEditor } from "../../bezier/useBezierEditor";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { GridState, HoverElement } from "../../types";
import { getNodeIcon } from "../../utils/nodeIcons";
import { getOrderedSourceNodes } from "../../utils/sourceOrder";
import { HourlyData, HourlyDispatchResult } from "../../powerFlow/multiTimestepOptimization";
import {
  renderGrid,
  renderAxes,
  renderControlPoints,
  generateCurvePath,
  renderCurrentTimeIndicator,
  renderStackedAreas,
  renderInfeasibleRegions,
} from "../../bezier/curveRenderer";

interface Props {
  controlPoints: BezierCurve;
  onControlPointsChange: (controlPoints: BezierCurve) => void;
  gridState?: GridState;
  onHourHover?: (hour: number | null) => void;
  hoverHour?: number | null;
  width?: number;
  height?: number;
  hourlyData: HourlyData[];
  hourlyGridStates?: HourlyDispatchResult[];
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
}

function LoadCurve({
  controlPoints,
  onControlPointsChange,
  gridState,
  onHourHover,
  hoverHour,
  width,
  height,
  hourlyData,
  hourlyGridStates,
  hoverElement,
  onElementHover,
}: Props) {
  // Define color palette for generators
  const generatorColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FECA57",
    "#FF9FF3",
    "#54A0FF",
    "#5F27CD",
    "#00D2D3",
    "#FF9F43",
  ];
  // Use container-based dimensions if not specified
  const containerRef = useRef<HTMLDivElement>(null);
  const legendWidth = 120;
  const dimensions = useElementDimensions(containerRef, width, height, {
    defaultWidth: 600 - legendWidth, // Account for legend width
    defaultHeight: 200,
    widthOffset: legendWidth + 8, // Account for legend width + gap
    heightOffset: 0,
  });

  const actualWidth = dimensions.width;
  const actualHeight = dimensions.height;
  const margin = 40;
  const leftMargin = 60;
  const timeScale = 24;
  const loadScaleMW = 200; // 0-200 MW scale

  // Use the hourly data passed in from parent
  const computedHourlyData = hourlyData || [];


  const {
    svgRef,
    dragging,
    timeToX,
    loadToY,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleHover,
    handleMouseLeave,
  } = useBezierEditor({
    controlPoints,
    onControlPointsChange,
    width: actualWidth,
    height: actualHeight,
    leftMargin: leftMargin,
    rightMargin: margin,
    timeScale,
    loadScale: loadScaleMW,
    onHover: onHourHover,
  });

  return (
    <div
      style={{
        padding: "8px",
        background: "#f9f9f9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#333" }}>
        Load Curve Editor
      </h3>

      <div
        ref={containerRef}
        style={{ 
          flex: 1, 
          position: "relative", 
          minHeight: 0,
          display: "flex",
          gap: "8px"
        }}
      >
        <div style={{ flex: 1, position: "relative" }}>
          <svg
            ref={svgRef}
            width={actualWidth}
            height={actualHeight}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              border: "1px solid #ddd",
              background: "white",
              cursor: dragging ? "grabbing" : "default",
              width: "100%",
              height: "100%",
            }}
            onMouseMove={(e) => {
              handleMouseMove(e);
              if (!dragging) {
                handleHover(e);
              }
            }}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
              handleMouseUp();
              handleMouseLeave();
            }}
          >
          {/* Grid */}
          {renderGrid({
            timeToX,
            valueToY: loadToY,
            actualWidth,
            actualHeight,
            margin,
            timeScale,
            valueScale: loadScaleMW,
            leftMargin,
          })}

          {/* Infeasible regions */}
          {renderInfeasibleRegions({
            hourlyData: computedHourlyData,
            timeToX,
            actualHeight,
            margin,
            leftMargin,
          })}

          {/* Axes */}
          {renderAxes({
            timeToX,
            valueToY: loadToY,
            actualWidth,
            actualHeight,
            margin,
            timeScale,
            valueScale: loadScaleMW,
            valueLabel: "Load Demand (MW)",
            valueUnits: "",
            leftMargin,
          })}

          {/* Stacked area chart for generators */}
          {renderStackedAreas({
            gridState,
            hourlyData: computedHourlyData,
            hourlyGridStates,
            timeToX,
            loadToY,
            colorPalette: generatorColors,
            hoverElement,
            onElementHover,
          })}

          {/* Load curve */}
          <path
            d={generateCurvePath({
              controlPoints,
              timeToX,
              valueToY: loadToY,
              timeScale,
              extendToTimeScale: true,
            })}
            fill="none"
            stroke="#2196F3"
            strokeWidth={3}
          />

          {/* Control points */}
          {renderControlPoints({
            controlPoints,
            timeToX,
            valueToY: loadToY,
            handleMouseDown,
          })}

          {/* Hover hour indicator */}
          {renderCurrentTimeIndicator({
            hoverHour,
            timeToX,
            margin,
            actualHeight,
          })}
        </svg>
        </div>

        {/* Generator legend - moved to side */}
        <div style={{ 
          width: `${legendWidth}px`, 
          minWidth: `${legendWidth}px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          paddingTop: "20px"
        }}>
          {renderSourceLegend({
            gridState,
            colorPalette: generatorColors,
            hoverElement,
            onElementHover,
          })}
        </div>
      </div>
    </div>
  );
}

interface RenderSourceLegendProps {
  gridState?: GridState;
  colorPalette: string[];
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
}

function renderSourceLegend({
  gridState,
  colorPalette,
  hoverElement,
  onElementHover,
}: RenderSourceLegendProps): React.ReactElement | null {
  if (!gridState) return null;

  const sourceNodes = getOrderedSourceNodes(gridState);

  if (sourceNodes.length === 0) return null;

  return (
    <div style={{ fontSize: "10px", color: "#666" }}>
      <p style={{ margin: "0 0 8px 0", fontWeight: "bold", fontSize: "10px" }}>
        Sources:
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {sourceNodes.map((sourceNode, index) => {
          const isHighlighted =
            hoverElement?.type === "Node" && hoverElement?.id === sourceNode.id;
          return (
          <div
            key={sourceNode.id}
            onMouseEnter={() =>
              onElementHover({ type: "Node", id: sourceNode.id })
            }
            onMouseLeave={() => onElementHover(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              backgroundColor: isHighlighted ? "#FFD70030" : "transparent",
              borderRadius: "2px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: isHighlighted
                  ? "#FFD700"
                  : colorPalette[index % colorPalette.length],
                borderRadius: "1px",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "9px", lineHeight: "1.2" }}>
              {getNodeIcon(sourceNode)} {sourceNode.id}{" "}
              {sourceNode.type === "battery" ? "(bat)" : ""}
            </span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default LoadCurve;
